import { Badge } from '@apps/web/components/ui/badge';
import { formatTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { type OutreachStatus } from '@libraries/typescript/api_client';

/**
 * Format a clock time string for display. Handles three formats:
 * - UTC ISO strings with timezone (new): formatted in org timezone
 * - Timezone-less ISO strings (legacy WellSky): treated as UTC
 * - Pre-formatted time strings like "5:00 PM" (legacy): shown as-is
 */
function formatClockTime(raw: string, timezone: string): string {
    // Already a pre-formatted time string (e.g. "5:00 PM")
    if (/^\d{1,2}:\d{2}\s*[AP]M$/i.test(raw)) {
        return raw;
    }
    // Timezone-less ISO string — append Z to treat as UTC.
    // Check for offset patterns: +HH:MM, -HH:MM, or trailing Z.
    const hasTimezone = /[+-]\d{2}:\d{2}$/.test(raw) || raw.endsWith('Z');
    const iso = !hasTimezone && raw.includes('T') ? `${raw}Z` : raw;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) {
        return raw;
    }
    return formatTime(iso, timezone);
}

type ClockReminderOutcomeBadgeProps = {
    readonly outreachType: 'clock_in' | 'clock_out';
    readonly status: OutreachStatus;
    readonly actualClockTime: string | null;
    readonly timezone: string;
};

export function ClockReminderOutcomeBadge({
    outreachType,
    status,
    actualClockTime,
    timezone,
}: ClockReminderOutcomeBadgeProps) {
    if (status !== 'filled' && status !== 'unfilled') {
        return null;
    }

    const isFilled = status === 'filled';
    const isClockIn = outreachType === 'clock_in';

    const label = isFilled
        ? isClockIn
            ? actualClockTime
                ? `Clocked in at ${formatClockTime(actualClockTime, timezone)}`
                : 'Clocked in'
            : actualClockTime
              ? `Clocked out at ${formatClockTime(actualClockTime, timezone)}`
              : 'Clocked out'
        : isClockIn
          ? 'Did not clock in'
          : 'Did not clock out';

    return (
        <Badge
            className={cn(
                'border-0',
                isFilled
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700',
            )}
        >
            {label}
        </Badge>
    );
}
